import type { IncomeDto, PersonDto } from "./benefit-types"
import { incomeHistoryLabels, monthlyDivisor } from "./income"

/** Normalize reported amounts to the same monthly basis before comparing adjustments. */
export function savedMonthlyIncome(income: IncomeDto): number {
  return income.incomeAmountCollection.reduce((sum, item) => sum + item.amount, 0) / monthlyDivisor(income.incomeType)
}

/** Match BenefitIncomeMath, including legacy combinations of adjustment flags. */
export function savedMonthlyAdjustedIncome(income: IncomeDto): number {
  const gross = income.incomeAmountCollection.reduce((sum, item) => sum + item.amount, 0)
  return gross * (1 - (income.a1 ? 0.2 : 0) - (income.a3 ? 0.1 : 0)) / monthlyDivisor(income.incomeType)
}

export function householdIncomeTypeLabel(people: readonly Pick<PersonDto, "incomeCollection">[]): string {
  const types = [...new Set(people.flatMap(person => person.incomeCollection.map(income => income.incomeType)))]
  if (types.length === 0) return "Not selected"
  if (types.length > 1) return "Mixed"
  return incomeHistoryLabels[types[0]!] ?? `Type ${types[0]}`
}

/** Oldest first, unknown dates last; never mutate the response collection or lose member identity. */
export function incomeMembersByDob<T extends Pick<PersonDto, "id" | "firstName" | "lastName"> & { birthDate: string | null }>(people: readonly T[]): T[] {
  const date = (person: T) => person.birthDate?.slice(0, 10) || "9999-12-31"
  return [...people].sort((a, b) => date(a).localeCompare(date(b))
    || a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName) || a.id.localeCompare(b.id))
}

/** List observed months explicitly: historical rows need not cover consecutive months. */
export function reportedIncomeMonths(amounts: readonly { date: string }[]): string {
  const months = [...new Set(amounts.map((item) => item.date.slice(0, 7)))].sort()
  return months.length === 0 ? "—" : months.join(", ")
}

/** A single-choice editor cannot round-trip a legacy mixed adjustment bit set. */
export function hasMixedIncomeAdjustments(income: IncomeDto): boolean {
  return [income.a1, income.a2, income.a3, income.aNone].filter(Boolean).length > 1
}
